'use client';

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { X, Leaf, FlaskRound, Shield, Award, Sparkles, Factory, Beaker, CheckCircle2, TrendingUp, Microscope, Zap, Snowflake, Users, Target, ArrowRight, Star } from 'lucide-react';
import Image from 'next/image';

interface HowWeElevateHempModalProps {
  children: React.ReactNode;
  className?: string;
}

export function HowWeElevateHempModal({ children, className = '' }: HowWeElevateHempModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  // allow external triggers
  React.useEffect(() => {
    const open = () => setIsOpen(true)
    window.addEventListener('open-how-we-elevate-hemp', open as EventListener)
    return () => window.removeEventListener('open-how-we-elevate-hemp', open as EventListener)
  }, [])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild className={className}>
        {children}
      </DialogTrigger>
      <DialogContent 
        hideOverlay
        className="w-[95vw] sm:w-[calc(100vw-32px)] max-w-[95vw] sm:max-w-[calc(100vw-32px)] max-h-[calc(100vh-60px)] sm:max-h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden rounded-lg sm:rounded-2xl p-3 sm:p-6 bg-black/30 backdrop-blur-xl border border-white/10"
        style={{
          boxShadow: '0 8px 32px 0 rgba(15, 20, 25, 0.37), 0 0 30px rgba(215, 164, 73, 0.3)',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}
      >
        <VisuallyHidden>
          <DialogTitle>How We Elevate Hemp</DialogTitle>
        </VisuallyHidden>
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-eskerium-gold via-yellow-300 to-eskerium-gold bg-clip-text text-transparent">
                How We Elevate Hemp
              </h2>
              <p className="text-sm sm:text-base text-white/70 mt-1">
                Setting a new standard in Live Resin & Hemp Extraction
              </p>
            </div>
            <Button
              onClick={() => setIsOpen(false)}
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Main content */}
          <div className="space-y-6 sm:space-y-10 w-full overflow-x-hidden">
            {/* Hero Section - Full Width Bento Card */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-white/20 transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-eskerium-gold/10 via-transparent to-eskerium-blue/10" />
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                    <span className="text-eskerium-gold italic text-lg sm:text-xl lg:text-2xl">Setting a new standard in</span>
                    <br />
                    Live Resin & Hemp Extraction.
                  </h3>
                  <p className="text-white/80 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6">
                    Our proprietary process preserves the full potential of the hemp plant, creating extracts that exceed industry norms for purity, flavor, and bioavailability.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <Star className="w-5 h-5 text-eskerium-gold" />
                      <span className="text-sm text-white/90">Your partner in success</span>
                    </div>
                  </div>
                </div>
                <div className="relative h-48 sm:h-64 lg:h-80 rounded-lg overflow-hidden">
                  <Image
                    src="/images/extract-close-2.jpg"
                    alt="Live Resin Extract"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
              </div>
            </div>

            {/* Quality Comparison Section - Responsive Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-green-500/30 transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-4">Our Full-Spectrum Live Resin</h4>
                <div className="relative h-48 rounded-lg overflow-hidden mb-4">
                  <Image
                    src="/images/61f5cc833f7bdaff84f4b6f7_sour-space-candy.jpg"
                    alt="Live Resin"
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white/90">Vibrant terpene profile</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white/90">True-to-plant essence</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white/90">Enhanced entourage effect</span>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-500/10 to-gray-500/5 border border-gray-500/20 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-gray-500/30 transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-4">Crude Full-Spectrum Oil</h4>
                <div className="relative h-48 rounded-lg overflow-hidden mb-4">
                  <Image
                    src="/images/Copy-of-Copy-of-crude.png"
                    alt="Crude Oil"
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="space-y-2 opacity-60">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-gray-500/30" />
                    <span className="text-sm text-white/70 line-through">Limited terpene retention</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-gray-500/30" />
                    <span className="text-sm text-white/70 line-through">Milder flavor profile</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-gray-500/30" />
                    <span className="text-sm text-white/70 line-through">Standard effects</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Clear Choice Card */}
            <div className="rounded-xl bg-gradient-to-br from-eskerium-gold/10 to-yellow-500/5 border border-eskerium-gold/20 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-eskerium-gold/30 transition-all duration-300">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-eskerium-gold mb-4">
                The Clear Choice in Purity, Flavor, Aroma and Experience
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Full-Spectrum Oil</h4>
                  <p className="text-white/70 text-sm">
                    Lacks the vibrant terpene profile of live resin, resulting in a milder flavor, aroma and overall effect.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-eskerium-gold mb-2">Live Resin Extract</h4>
                  <p className="text-white/90 text-sm">
                    Captures a rich, true-to-plant essence, delivering an intense and authentic sensory experience with enhanced terpene and cannabinoid concentration.
                  </p>
                </div>
              </div>
            </div>

            {/* Entourage Effect Section - 3 Cards */}
            <div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-4 sm:mb-6">
                Unlock the power of <span className="text-eskerium-gold italic">the Entourage Effect</span>
              </h3>
              <p className="text-white/70 mb-6">
                The Entourage Effect is the amplified result when cannabinoids, terpenes, and other compounds in hemp work together. 
                By capturing the full spectrum, Eskerium's extracts provide deeper wellness benefits that <strong>isolated compounds can't match</strong>.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/5 border border-purple-500/20 backdrop-blur-md p-7 hover:border-purple-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="text-3xl font-bold text-purple-400 mb-3">1</div>
                  <h4 className="text-lg font-semibold text-white mb-2">Synergy for Superior Potency</h4>
                  <p className="text-white/70 text-sm">
                    When cannabinoids and terpenes work together, they create stronger effects. Our full-spectrum extracts harness this synergy.
                  </p>
                </div>

                <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 backdrop-blur-md p-7 hover:border-blue-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="text-3xl font-bold text-blue-400 mb-3">2</div>
                  <h4 className="text-lg font-semibold text-white mb-2">True-to-Plant Experience</h4>
                  <p className="text-white/70 text-sm">
                    Our process keeps the plant's natural balance intact, delivering authentic flavor and effects that stand out.
                  </p>
                </div>

                <div className="rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 backdrop-blur-md p-7 hover:border-green-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="text-3xl font-bold text-green-400 mb-3">3</div>
                  <h4 className="text-lg font-semibold text-white mb-2">Boosted Customer Loyalty</h4>
                  <p className="text-white/70 text-sm">
                    Consumers value full-spectrum potency. With Eskerium's extracts, you offer a premium experience that builds trust.
                  </p>
                </div>
              </div>
            </div>

            {/* Advanced Cryogenic Process Section */}
            <div>
              <div className="text-center mb-6">
                <p className="text-eskerium-gold text-sm font-semibold mb-2">THE ESKERIUM WAY</p>
                <h3 className="text-2xl font-bold text-white">Our Advanced Cryogenic Extraction Process</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 backdrop-blur-md overflow-hidden hover:border-blue-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="relative h-40 overflow-hidden">
                    <Image
                      src="/images/frozen-hemp.png"
                      alt="Flash-Freezing"
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-2 mb-3">
                      <Snowflake className="w-5 h-5 text-blue-400" />
                      <h4 className="text-lg font-semibold text-white">Flash-Freezing at Harvest</h4>
                    </div>
                    <p className="text-white/70 text-sm mb-4">
                      Immediately after harvest, we flash-freeze the hemp flowers to lock in the plant's natural compounds at peak freshness.
                    </p>
                    <div className="flex items-center gap-2 text-blue-400">
                      <ArrowRight className="w-4 h-4" />
                      <span className="text-sm font-medium">Preserving Nature's Full Potential</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/5 border border-purple-500/20 backdrop-blur-md overflow-hidden hover:border-purple-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="relative h-40 overflow-hidden">
                    <Image
                      src="/images/Screenshot-2024-11-06-at-11.59.50-AM.png"
                      alt="Cryogenic Extraction"
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-2 mb-3">
                      <Beaker className="w-5 h-5 text-purple-400" />
                      <h4 className="text-lg font-semibold text-white">Cryogenic Extraction</h4>
                    </div>
                    <p className="text-white/70 text-sm mb-4">
                      Using cryogenic temperatures, our extraction process isolates the plant's beneficial compounds without degrading them.
                    </p>
                    <div className="flex items-center gap-2 text-purple-400">
                      <ArrowRight className="w-4 h-4" />
                      <span className="text-sm font-medium">Uncompromised Potency in Every Drop</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 backdrop-blur-md overflow-hidden hover:border-green-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="relative h-40 overflow-hidden">
                    <Image
                      src="/images/61f5cc833f7bdaff84f4b6f7_sour-space-candy.jpg"
                      alt="Quality Assurance"
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-green-400" />
                      <h4 className="text-lg font-semibold text-white">Quality Assurance</h4>
                    </div>
                    <p className="text-white/70 text-sm mb-4">
                      We refine and test the product rigorously. Each batch is third-party tested for potency, purity, and consistency.
                    </p>
                    <div className="flex items-center gap-2 text-green-400">
                      <ArrowRight className="w-4 h-4" />
                      <span className="text-sm font-medium">Premium Quality You Can Stand Behind</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quality Standards Section with Quotes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Aki Quote Card */}
              <div className="rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/5 border border-purple-500/20 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-purple-500/30 transition-all duration-300">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="relative w-32 sm:w-40 lg:w-48 h-48 sm:h-56 lg:h-64 flex-shrink-0 rounded-lg overflow-hidden">
                    <Image
                      src="/images/aki_mix.jpg"
                      alt="Aki Smith"
                      fill
                      className="object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/90 italic text-lg mb-6">
                      "The terpene profile in our live resin is incredibly diverse, capturing the true spectrum of the plant's aromatic and therapeutic compounds. This richness not only enhances flavor but also amplifies the entourage effect."
                    </p>
                    <p className="text-eskerium-gold font-semibold text-lg">- Aki Smith</p>
                    <p className="text-white/70 text-sm">Chief Science Officer</p>
                  </div>
                </div>
              </div>

              {/* Devin Quote Card */}
              <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-blue-500/30 transition-all duration-300">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="relative w-32 sm:w-40 lg:w-48 h-48 sm:h-56 lg:h-64 flex-shrink-0 rounded-lg overflow-hidden">
                    <Image
                      src="/images/devin_forklift.jpg"
                      alt="Devin Christy"
                      fill
                      className="object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/90 italic text-lg mb-6">
                      "We believe in transparency and collaboration. Working closely with our partners allows us to craft solutions that meet their exact needs and elevate their products in a crowded market."
                    </p>
                    <p className="text-eskerium-gold font-semibold text-lg">– Devin Christy</p>
                    <p className="text-white/70 text-sm">Founder & CEO</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Beliefs and Mission Section */}
            <div className="rounded-xl bg-gradient-to-br from-eskerium-gold/10 to-yellow-500/5 border border-eskerium-gold/20 backdrop-blur-md p-4 sm:p-6 lg:p-8 hover:border-eskerium-gold/30 transition-all duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-eskerium-gold text-sm font-semibold mb-3">OUR BELIEFS AND MISSION</p>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Founded on Excellence, Driven by Innovation
                  </h3>
                  <p className="text-white/80 mb-6">
                    Founded on the belief that hemp can provide true wellness solutions, Eskerium combines <strong className="text-white">cutting-edge science</strong> with <strong className="text-white">deep industry knowledge</strong> to produce live resin products that are as effective as they are authentic.
                  </p>
                  <p className="text-white/80">
                    By choosing Eskerium, you join a mission dedicated to advancing the quality of life through better hemp products, creating real, lasting impact.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-white mb-6">Your Journey with Us</h4>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-eskerium-gold/20 border border-eskerium-gold/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-eskerium-gold">1</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-white mb-1">Discovery Call</h5>
                        <p className="text-white/70 text-sm">Identify your specific needs and goals with a personalized consultation.</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-eskerium-gold/20 border border-eskerium-gold/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-eskerium-gold">2</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-white mb-1">Proposal</h5>
                        <p className="text-white/70 text-sm">Receive a detailed proposal tailored to your product vision.</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-eskerium-gold/20 border border-eskerium-gold/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-eskerium-gold">3</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-white mb-1">Dedicated Project Manager</h5>
                        <p className="text-white/70 text-sm">Experience seamless project management from start to finish.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="rounded-xl bg-gradient-to-br from-eskerium-gold/20 to-yellow-500/10 border border-eskerium-gold/30 backdrop-blur-md p-8 text-center hover:border-eskerium-gold/40 transition-all duration-300">
              <h3 className="text-3xl font-bold text-eskerium-gold mb-3">
                Ready to Elevate Your Products?
              </h3>
              <p className="text-white/80 text-lg mb-6">
                Gold Standard Live Resin - Your competitive edge.
              </p>
              <div className="flex gap-4 justify-center">
                {/* Contact Us opens Contact Support modal */}
                <Button 
                  onClick={() => {
                    setIsOpen(false);
                    // open contact modal by navigating to portal contact hash or emitting custom event
                    window.dispatchEvent(new CustomEvent('open-contact-support'))
                  }}
                  className="bg-gradient-to-r from-eskerium-gold to-yellow-500 text-eskerium-dark hover:from-yellow-500 hover:to-eskerium-gold font-semibold px-8"
                >
                  Contact Us
                </Button>
                {/* View Products opens Public Inventory Browser */}
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => {
                    setIsOpen(false);
                    window.dispatchEvent(new CustomEvent('open-public-inventory'));
                  }}
                >
                  View Products
                </Button>
              </div>
              <div className="flex items-center justify-center gap-2 mt-6">
                <Star className="w-5 h-5 text-eskerium-gold" />
                <span className="text-sm text-white/90 font-medium">Receive a solution tailored to meet your exact needs</span>
              </div>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
}

export default HowWeElevateHempModal;